/**
 * Speech engine built on the browser's built-in SpeechSynthesis API.
 *
 * This is the baseline that works with no extra software. Whether an Arabic
 * voice exists depends on the operating system — see README → "Arabic speech".
 *
 * Two well-known browser problems are handled here:
 *   1. The voice list arrives asynchronously (and sometimes twice).
 *   2. Chrome stops speaking after about 15 seconds. Long text is therefore cut
 *      into short chunks, and a periodic resume() keeps the queue alive.
 */
import type { SpeakOptions, SpeechEngine, TtsVoice } from './types';

/** Chunk length chosen so one chunk never approaches Chrome's 15 s cut-off. */
const MAX_CHUNK_CHARS = 140;
const KEEPALIVE_MS = 8000;

function splitIntoChunks(text: string): string[] {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= MAX_CHUNK_CHARS) return clean ? [clean] : [];

  // Prefer to break at sentence ends, then at commas, then at spaces.
  const parts: string[] = [];
  let rest = clean;
  const breakers = ['۔', '.', '؟', '?', '!', '،', ',', ';', ':'];

  while (rest.length > MAX_CHUNK_CHARS) {
    const window = rest.slice(0, MAX_CHUNK_CHARS);
    let cut = -1;
    for (const b of breakers) {
      const idx = window.lastIndexOf(b);
      if (idx > cut) cut = idx;
    }
    if (cut < MAX_CHUNK_CHARS * 0.4) cut = window.lastIndexOf(' ');
    if (cut <= 0) cut = MAX_CHUNK_CHARS - 1;
    parts.push(rest.slice(0, cut + 1).trim());
    rest = rest.slice(cut + 1).trim();
  }
  if (rest) parts.push(rest);
  return parts.filter(Boolean);
}

export class WebSpeechEngine implements SpeechEngine {
  readonly id = 'web-speech' as const;
  readonly displayNameEn = 'Browser voice';
  readonly displayNameAr = 'صوت المتصفح';
  readonly supportsPause = true;

  private voices: TtsVoice[] = [];
  private listeners = new Set<() => void>();
  private keepAlive: number | null = null;
  private userPaused = false;

  private get synth(): SpeechSynthesis | null {
    return typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  }

  async init(): Promise<void> {
    const synth = this.synth;
    if (!synth) return;

    const refresh = () => {
      this.voices = synth.getVoices().map(toTtsVoice);
      this.listeners.forEach((l) => l());
    };

    refresh();
    synth.addEventListener('voiceschanged', refresh);

    // Some browsers populate the list a moment after the first call.
    if (this.voices.length === 0) {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        const timer = window.setInterval(() => {
          refresh();
          if (this.voices.length > 0) {
            window.clearInterval(timer);
            finish();
          }
        }, 150);
        window.setTimeout(() => {
          window.clearInterval(timer);
          finish();
        }, 3000);
      });
    }
  }

  getVoices(): TtsVoice[] {
    return this.voices;
  }

  onVoicesChanged(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  speak(text: string, options: SpeakOptions): Promise<void> {
    const synth = this.synth;
    if (!synth) return Promise.resolve();

    const chunks = splitIntoChunks(text);
    if (chunks.length === 0) return Promise.resolve();

    const nativeVoice = this.resolveVoice(options);
    this.startKeepAlive();

    return new Promise<void>((resolve) => {
      let index = 0;
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        options.signal.removeEventListener('abort', onAbort);
        this.stopKeepAlive();
        resolve();
      };

      const onAbort = () => {
        synth.cancel();
        finish();
      };

      if (options.signal.aborted) {
        finish();
        return;
      }
      options.signal.addEventListener('abort', onAbort);

      const speakNext = () => {
        if (settled || options.signal.aborted) {
          finish();
          return;
        }
        if (index >= chunks.length) {
          finish();
          return;
        }
        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        index += 1;
        utterance.rate = clampRate(options.rate);
        utterance.pitch = 1;
        utterance.volume = 1;
        utterance.lang = nativeVoice?.lang ?? (options.lang === 'ar' ? 'ar' : 'en-US');
        if (nativeVoice) utterance.voice = nativeVoice;

        utterance.onend = () => speakNext();
        utterance.onerror = (event) => {
          // "interrupted"/"canceled" happen whenever we stop on purpose.
          const reason = (event as SpeechSynthesisErrorEvent).error;
          if (reason && reason !== 'interrupted' && reason !== 'canceled') {
            console.warn('[tts] speech error:', reason);
          }
          finish();
        };

        synth.speak(utterance);
      };

      // A pending cancel from a previous utterance can swallow the next one.
      synth.cancel();
      window.setTimeout(speakNext, 30);
    });
  }

  pause(): void {
    this.userPaused = true;
    this.synth?.pause();
  }

  resume(): void {
    this.userPaused = false;
    this.synth?.resume();
  }

  cancel(): void {
    this.userPaused = false;
    this.stopKeepAlive();
    this.synth?.cancel();
  }

  private resolveVoice(options: SpeakOptions): SpeechSynthesisVoice | null {
    const synth = this.synth;
    if (!synth) return null;
    const all = synth.getVoices();
    if (all.length === 0) return null;

    if (options.voiceId) {
      const exact = all.find((v) => voiceKey(v) === options.voiceId);
      if (exact) return exact;
    }
    const wanted = options.lang === 'ar' ? isArabicTag : isEnglishTag;
    const matches = all.filter((v) => wanted(v.lang));
    if (matches.length === 0) {
      // IMPORTANT: we never substitute a voice of the wrong language.
      return null;
    }
    return matches.find((v) => v.default) ?? matches[0];
  }

  private startKeepAlive(): void {
    if (this.keepAlive !== null) return;
    this.keepAlive = window.setInterval(() => {
      const synth = this.synth;
      if (!synth) return;
      if (!this.userPaused && synth.speaking) synth.resume();
    }, KEEPALIVE_MS);
  }

  private stopKeepAlive(): void {
    if (this.keepAlive !== null) {
      window.clearInterval(this.keepAlive);
      this.keepAlive = null;
    }
  }
}

export function voiceKey(v: SpeechSynthesisVoice): string {
  return v.voiceURI || `${v.name}::${v.lang}`;
}

export function isArabicTag(lang: string): boolean {
  return /^ar(-|_|$)/i.test(lang.trim());
}

export function isEnglishTag(lang: string): boolean {
  return /^en(-|_|$)/i.test(lang.trim());
}

function toTtsVoice(v: SpeechSynthesisVoice): TtsVoice {
  return {
    id: voiceKey(v),
    name: v.name,
    lang: v.lang,
    isArabic: isArabicTag(v.lang),
    isEnglish: isEnglishTag(v.lang),
    isDefault: v.default,
  };
}

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  return Math.min(2, Math.max(0.4, rate));
}
